/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable max-params */
import isNil from 'lodash.isnil';
import isObject from 'lodash.isobject';
import {DmUnit, HVSRecord} from 'types/entities';
import {translate} from '../locale/locale';
import {error, info} from '../logger/logger';
import {
  ALTITUDE_CONVERSIONS,
  Attribute,
  DISTANCE_CONVERSIONS,
  getAttributeLabel,
  getUnitByAttribute as unitFromAttribute,
  HEIGHT_CONVERSIONS,
  Measurement,
  ODOMETER_CONVERSIONS,
  REVERSE_ODOMETER_CONVERSIONS,
  SPEED_CONVERSIONS,
  VOLUME_CONVERSIONS,
  WEIGHT_CONVERSIONS
} from './attributes';
import {
  kilometersPerHourToMilesPerHour,
  milesPerHourToKilometersPerHour
} from './conversions';
import Unit, {isValidUnit} from './units';

/**
 * Defines the conversion parameters used by the various conversion functions.
 */
export type ConversionParameters = number | {
  val: number, from?: Unit, to?: Unit
}

/**
 * Defines the attribute like type which describes the various conversion
 * attributes which may exist on an entity which is to be converted.
 */
export interface AttributeLike {
  systemOfUnits: string;
  gpsSpeed: number;
  gpsOdometer: number;
  vbSpeed: number;
  vbOdometer: number;
  altitude: number;
  [key: string]: any;
}

/**
 * Defines the event like type which describes the various conversion
 * attributes which may exist on an event based entity (such as an AVL)
 * event which is to be converted.
 */
export interface EventLike {
  postedSpeedLimit: number;
  postedSpeedLimitUnits: string;
  [key: string]: any;
}

/**
 * Defines the return value of the getEventSpeed() function.
 */
export type EventSpeed = {
  speed: number,
  overSpeed: number,
  units: string
};

/**
 * @private
 * @default Unit.US
 *
 * Defines the authenticate User's Unit of Measure. This value determines
 * the conversions which are to be made against the system of units of all
 * entity Attributes. Defaults to US.
 */
let _userUOM = Unit.US;

/**
 * Returns the application wide Unit of Measure as specified via a call
 * to the setUserUnit function.
 *
 * ```typescript
 *   import {getUserUnit} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   const uom: Unit = getUserUnit();
 *   info(`Application UOM: [${uom}]`);
 *   info(user.systemOfUnit.value === uom); // true
 *
 * ```
 *
 * @returns {string} The authenticated User's Unit of Measure.
 */
export const getUserUnit = (): Unit => {
  return _userUOM;
};

/**
 * Provides the entry point unto the Units API, which allows for specifying
 * the specific Unit of Measure to which all conversions are to be made. This
 * function should be invoked upon successful receiving the authenticated User
 * entity from CTC Services.
 *
 * ```typescript
 *   import {setUserUnit} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *   import {User} from '@ion/core/src/types/entities';
 *
 *   // retrieve authenticated user and set uom ...
 *   const user: User = await currentUser();
 *   setUserUnit(user.systemOfUnit.value);
 *   info(`Application UOM: [${getUserUnit()}]`);
 *   info(user.systemOfUnit.value === getUserUnit()); // true
 *
 * ```
 *
 * @param    {string} uom The authenticated User's Unit of Measure. Valid values
 *           are defined within the units module, and include the following units:
 *           US, METRIC, DEVICE, MIXED, and SI.
 */
export const setUserUnit = (uom: Unit): void => {
  if (isValidUnit(uom)) {
    _userUOM = uom;
    info(`User Units of Measurement: ${uom}`);
  } else {
    error(`[Units API]: Invalid User Unit of Measure provided: "${uom}". Using default: ${_userUOM}`);
  }
};

const DM_UNIT_MAP: Record<Unit, DmUnit> = {
  [Unit.US]       : 'US',
  [Unit.IMPERIAL] : 'UK',
  [Unit.METRIC]   : 'Metric',
  [Unit.SI]       : 'Metric',
  [Unit.MIXED]    : 'Metric',
  [Unit.DEVICE]   : 'Device'
};

/**
 * Given an ADM Unit of Measure, returns the DM (PULS) Unit of Measure it
 * maps to. DM's vocabulary is a subset of ADM's, so `Imperial` collapses
 * to `UK`, and both `SI` and `Mixed` collapse to `Metric`.
 *
 * ```typescript
 *   import {toDmUnit} from '@ion/core/src/units/helpers';
 *   import {Unit} from '@ion/core/src/units/units';
 *   ...
 *
 *   toDmUnit(Unit.IMPERIAL); // 'UK'
 *
 * ```
 *
 * @param   {Unit} uom The ADM Unit of Measure to translate.
 * @returns {DmUnit} The corresponding DM Unit of Measure.
 */
export const toDmUnit = (uom: Unit): DmUnit => DM_UNIT_MAP[uom];

/**
 * Returns the current user's Unit of Measure translated to DM's (PULS)
 * vocabulary, for use in request parameters sent to DM-facing endpoints.
 *
 * ```typescript
 *   import {getDmUnit} from '@ion/core/src/units/helpers';
 *   ...
 *
 *   const uom: DmUnit = getDmUnit();
 *
 * ```
 *
 * @returns {DmUnit} The authenticated User's Unit of Measure, in DM's vocabulary.
 */
export const getDmUnit = (): DmUnit => toDmUnit(getUserUnit());

/**
 * Given an odometer value and the unit of measure from which the value
 * is to be converted from, and a unit of measure to which the value is
 * to be converted to, returns the converted odometer value.
 *
 * ```typescript
 *   import {convertOdometer} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // convert odometer from US to METRIC ...
 *   info(convertOdometer(1, Unit.US, Unit.METRIC)); // 1.6
 *
 *   // convert odometer from METRIC to US ...
 *   info(convertOdometer(1000, Unit.METRIC, Unit.US)); // 0.6
 *
 *   // convert odometer from METRIC to METRIC ...
 *   info(convertOdometer(1000, Unit.METRIC, Unit.METRIC)); // 1
 *
 *   // convert odometer from METRIC to US ...
 *   info(convertOdometer(1000, Unit.METRIC, Unit.US)); // 0.6
 *
 *   // convert odometer from METRIC to METRIC ...
 *   info(convertOdometer(1000, Unit.METRIC, Unit.METRIC)); // 1
 *
 *   // convert odometer via options argument ...
 *   info(convertOdometer({
 *     val  : 1,
 *     from : Unit.US,
 *     to   : Unit.METRIC
 *   }); // 1.6
 *
 * ```
 *
 *
 * @param   {string} val The odometer value which is to be converted
 *          from the provided unit of measure to the specific unit
 *          of measure.

 * @param   {string|object} val Either the 'odometer' value which is to
 *          be converted, or an object containing a 'val' property which
 *          contains the 'odometer' value to be converted, and a 'from'
 *          property which defines the the UOM from which the conversion
 *          is to be based, and a 'to' property which defines the UOM to
 *          which the conversion is to be made.
 * @param   {string} from the unit of measure which the odometer value
 *          is to be converted from.
 * @param   {string} to the unit of measure which the odometer value
 *          is to be converted to.
 * @param   {boolean} reverse whether to perform reverse conversion
 *          i.e. display units to service Unit.
 * @returns {string} The odometer value converted from the provided
 *          unit of measure to the specified unit of measure.
 */
export const convertOdometer = (
  val: ConversionParameters, from?: Unit, to?: Unit, reverse = false): number => {
  const params = convertParams(val, from, to);
  if (params) {
    const unit = reverse ? REVERSE_ODOMETER_CONVERSIONS[params.from] : ODOMETER_CONVERSIONS[params.from];
    if (unit) {
      const converter = unit[params.to];
      if (converter) {
        return converter(params.val);
      }
    }
  }
  return val as number;
};

/**
 * Given an distance value and the unit of measure from which the value
 * is to be converted from, and a unit of measure to which the value
 * is to be converted to, returns the converted distance value.
 *
 * ```typescript
 *   import {convertDistance} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // convert speed from US to METRIC ...
 *   info(convertDistance(10, Unit.METRIC, Unit.IMPERIAL)); // 6.21371
 *
 *   // convert speed from METRIC to US ...
 *   info(convertDistance(6.21371, Unit.IMPERIAL, Unit.METRIC)); // 10
 *
 *   // convert speed via options argument ...
 *   info(convertDistance({
 *     'val'  : 10,
 *     'from' : Unit.METRIC,
 *     'to'   : Unit.IMPERIAL
 *   }); // 6.21371
 *
 * ```
 */
export const convertDistance = (val: ConversionParameters, from?: Unit, to?: Unit): number => {
  const params = convertParams(val, from, to);
  if (params) {
    const unit = DISTANCE_CONVERSIONS[params.from];
    if (unit) {
      const converter = unit[params.to];
      if (converter) {
        return converter(params.val);
      }
    }
  }
  return val as number;
};

/**
 * Given an weight value and the unit of measure from which the value
 * is to be converted from, and a unit of measure to which the value
 * is to be converted to, returns the converted weight value.
 *
 * ```typescript
 *   import {convertWeight} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // convert speed from US to METRIC ...
 *   info(convertWeight(10, Unit.METRIC, Unit.IMPERIAL)); // 22.0462
 *
 *   // convert speed from METRIC to US ...
 *   info(convertWeight(22.0462, Unit.IMPERIAL, Unit.METRIC)); // 10
 *
 *   // convert speed via options argument ...
 *   info(convertWeight({
 *     'val'  : 10,
 *     'from' : Unit.METRIC,
 *     'to'   : Unit.IMPERIAL
 *   }); // 22.0462
 *
 * ```
 */
export const convertWeight = (val: ConversionParameters, from?: Unit, to?: Unit): number => {
  const params = convertParams(val, from, to);
  if (params) {
    const unit = WEIGHT_CONVERSIONS[params.from];
    if (unit) {
      const converter = unit[params.to];
      if (converter) {
        return converter(params.val);
      }
    }
  }
  return val as number;
};

/**
 * Given an volume value and the unit of measure from which the value
 * is to be converted from, and a unit of measure to which the value
 * is to be converted to, returns the converted volume value.
 *
 * ```typescript
 *   import {convertVolume} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // convert speed from US to METRIC ...
 *   info(convertVolume(10, Unit.METRIC, Unit.IMPERIAL)); // 2.6
 *
 *   // convert speed from METRIC to US ...
 *   info(convertVolume(2.6, Unit.IMPERIAL, Unit.METRIC)); // 10
 *
 *   // convert speed via options argument ...
 *   info(convertVolume({
 *     'val'  : 10,
 *     'from' : Unit.METRIC,
 *     'to'   : Unit.IMPERIAL
 *   }); // 2.6
 *
 * ```
 */
export const convertVolume = (val: ConversionParameters, from?: Unit, to?: Unit): number => {
  const params = convertParams(val, from, to);
  if (params) {
    const unit = VOLUME_CONVERSIONS[params.from];
    if (unit) {
      const converter = unit[params.to];
      if (converter) {
        return converter(params.val);
      }
    }
  }
  return val as number;
};

/**
 * Given an volume value and the unit of measure from which the value
 * is to be converted from, and a unit of measure to which the value
 * is to be converted to, returns the converted volume value.
 *
 * ```typescript
 *   import {convertHeight} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // convert speed from US to METRIC ...
 *   info(convertHeight(10, Unit.METRIC, Unit.IMPERIAL)); // 3.9
 *
 *   // convert speed from METRIC to US ...
 *   info(convertHeight(25, Unit.IMPERIAL, Unit.METRIC)); // 63.5
 *
 *   // convert speed via options argument ...
 *   info(convertHeight({
 *     'val'  : 10,
 *     'from' : Unit.METRIC,
 *     'to'   : Unit.IMPERIAL
 *   }); // 3.9
 *
 * ```
 */
export const convertHeight = (val: ConversionParameters, from?: Unit, to?: Unit): number => {
  const params = convertParams(val, from, to);
  if (params) {
    const unit = HEIGHT_CONVERSIONS[params.from];
    if (unit) {
      const converter = unit[params.to];
      if (converter) {
        return converter(params.val);
      }
    }
  }
  return val as number;
};

/**
 * Given an speed value and the unit of measure from which the value
 * is to be converted from, and a unit of measure to which the value
 * is to be converted to, returns the converted speed value.
 *
 * ```typescript
 *   import {convertSpeed} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // convert speed from US to METRIC ...
 *   info(convertSpeed(20, Unit.US, Unit.METRIC)); // 32.2
 *
 *   // convert speed from METRIC to US ...
 *   info(convertSpeed(2000, Unit.METRIC, Unit.US)); // 1.2
 *
 *   // convert speed via options argument ...
 *   info(convertSpeed({
 *     'val'  : 20,
 *     'from' : Unit.US,
 *     'to'   : Unit.METRIC
 *   }); // 32.2
 *
 * ```
 *
 * @param   {string|object} val Either the 'speed' value which is to
 *          be converted, or an object which provides a 'val' property
 *          which contains the 'speed' value to be converted, a 'from'
 *          property which defines the the UOM from which the conversion
 *          is to be based, and a 'to' property which defines the UOM to
 *          which the conversion is to be made.
 * @param   {string} from the unit of measure which the speed value
 *          is to be converted from.
 * @param   {string} to the unit of measure which the speed value
 *          is to be converted to.
 * @returns {string} The speed value converted from the provided
 *          unit of measure to the specified unit of measure (optional).
 */
export const convertSpeed = (val: ConversionParameters, from?: Unit, to?: Unit): number => {
  const params = convertParams(val, from, to);
  if (params) {
    const unit = SPEED_CONVERSIONS[params.from];
    if (unit) {
      const converter = unit[params.to];
      if (converter) {
        return converter(params.val);
      }
    }
  }
  return val as number;
};

/**
 * Given an altitude value and the unit of measure from which the value
 * is to be converted from, and a unit of measure to which the value is
 * to be converted to, returns the converted altitude value.
 *
 * ```typescript
 *   import {convertAltitude} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // convert altitude from US to METRIC ...
 *   info(convertAltitude(100, Unit.US, Unit.METRIC)); // 30.5
 *
 *   // convert altitude from METRIC to US ...
 *   info(convertAltitude(100, Unit.METRIC, Unit.US)); // 328.1
 *
 *   // convert altitude via options argument ...
 *   info(convertAltitude({
 *     'val'  : 100,
 *     'from' : Unit.US,
 *     'to'   : Unit.METRIC
 *   }); // 30.5
 *
 * ```
 *
 * @param   {string|object} val Either the 'altitude' value which is to
 *          be converted, or an object which provides a 'val' property
 *          which contains the 'altitude' value to be converted, a 'from'
 *          property which defines the the UOM from which the conversion
 *          is to be based, and a 'to' property which defines the UOM to
 *          which the conversion is to be made.
 * @param   {string} from the unit of measure which the altitude value
 *          is to be converted from (optional).
 * @param   {string} to the unit of measure which the altitude value
 *          is to be converted to (optional).
 * @returns {string} The altitude value converted from the provided
 *          unit of measure to the specified unit of measure (optional).
 */
export const convertAltitude = (val: ConversionParameters, from?: Unit, to?: Unit): number => {
  const params = convertParams(val, from, to);
  if (params) {
    const unit = ALTITUDE_CONVERSIONS[params.from];
    if (unit) {
      const converter = unit[params.to];
      if (converter) {
        return converter(params.val);
      }
    }
  }
  return val as number;
};

/**
 * Converts a posted speed limit value on an entity based on the given UOM to which
 * the value is to be converted. An Events postedSpeedLimitUnits can only be 'mph'
 * or 'kph'. Likewise, the toUom can only be 'US' or 'Metric'
 *
 * ```typescript
 *   import {convertPSLUnit} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // event.postedSpeedLimit // 30 mph
 *   info(convertPSLUnit(event, 'Metric')); // 48.3 kph
 *
 * ```
 *
 * @param   {AttributeLike} event the avl event which is to be converted.
 * @param   {Unit} toUom The Unit the RSL is to be converted to.
 * @returns {number} The given event PSL converted to the requested Unit.
 */
export const convertPSLUnit = (event: EventLike, toUom: Unit): void => {
  const {postedSpeedLimit, postedSpeedLimitUnits} = event;
  if (postedSpeedLimitUnits) {
    if (postedSpeedLimitUnits === 'mph') {
      if (!isImperial(toUom)) {
        event.postedSpeedLimit = milesPerHourToKilometersPerHour(postedSpeedLimit);
        event.postedSpeedLimitUnits = 'kph';
      }
    } else if (isImperial(toUom)) {
      event.postedSpeedLimit = kilometersPerHourToMilesPerHour(postedSpeedLimit);
      event.postedSpeedLimitUnits = 'mph';
    }
  }
};

/**
 * Given a list of AVL Events, returns the list with all support entity
 * attributes converted to the User's given Unit of Measure.
 *
 * Note, only AVL Events which contain a 'systemOfUnits' property will
 * automatically be converted
 *
 * This function replaces the deprecated 'convertUnitsAvls' function.
 *
 * ```typescript
 *   import {convertAVLEvents, US, METRIC} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // conversions are automatically made based on the global UOM ...
 *   setUserUnit(METRIC);
 *   ...
 *
 *   // conversions are performed based on AVL Event systemOfUnits ...
 *   let events = [{
 *     systemOfUnits : US,
 *     gpsSpeed      : 20,
 *     gpsOdometer   : 1,
 *     vbSpeed       : 20,
 *     vbOdometer    : 1,
 *     altitude      : 100
 *   }, {
 *     systemOfUnits : US,
 *     gpsSpeed      : 30,
 *     gpsOdometer   : 2,
 *     vbSpeed       : 30,
 *     vbOdometer    : 2,
 *     altitude      : 200
 *   }];
 *
 *   // perform the conversions ...
 *   events = convertAVLEvents(events);
 *
 *   info(events[0].gpsSpeed)); // 32.2
 *   info(events[0].gpsOdometer)); //1.6
 *   info(events[0].vbSpeed)); //32.2
 *   info(events[0].vbOdometer)); //1.6
 *   info(events[0].altitude)); //30.5
 *   info(events[1].gpsSpeed)); // 48.3
 *   info(events[1].gpsOdometer)); //3.2
 *   info(events[1].vbSpeed)); //48.3
 *   info(events[1].vbOdometer)); //3.2
 *   info(events[1].altitude)); //61
 *
 * ```
 *
 * @param   {array} list The list of AVL Events which are to be converted.
 * @returns {array} The list of AVL Events with all supported attributes
 *          converted to the User's given Unit of Measure.
 */
export const convertAVLEvents = (list: unknown[]): unknown[] => {
  if (list.length) {
    return list.map(event => convertAVLEvent(event));
  }
  return list;
};

/**
 * Given an individual AVL Event, if the instance provides a systemOfUnits
 * property, converts the 'gpsSpeed', 'gpsOdometer', 'vbSpeed', 'vbOdometer',
 * and 'altitude' properties of the event from the event's systemOfUnits
 * to the User's Unit of Measure.
 *
 * ```typescript
 *   import {convertAVLEvent, US, METRIC} from '@ion/core/src/units/helpers';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // conversions are automatically made based on the global UOM ...
 *   setUserUnit(US);
 *   ...
 *
 *   // conversions are performed based on AVL Event systemOfUnits ...
 *   let event = {
 *     systemOfUnits : METRIC,
 *     gpsSpeed      : 2000,
 *     gpsOdometer   : 1000,
 *     vbSpeed       : 2000,
 *     vbOdometer    : 1000,
 *     altitude      : 100
 *   };
 *   // perform the conversions ...
 *   events = convertAVLEvent(events);
 *
 *   info(event.gpsSpeed)); //1.2
 *   info(event.gpsOdometer)); //0.6
 *   info(event.vbSpeed)); //1.2
 *   info(event.vbOdometer)); //0.6
 *   info(event.altitude)); //328.1
 *
 * ```
 *
 * This function replaces the deprecated 'convertUnitsAvl' function.
 *
 * @param   {array} list The list of AVL Events which are to be converted.
 * @returns {array} The list of AVL Events with all supported attributes
 *          converted to the User's given Unit of Measure.
 */
export const convertAVLEvent = (event: any): HVSRecord => {
  const {systemOfUnits} = event;
  if (!isNil(systemOfUnits)) {
    const {
      speed, Speed, gpsSpeed, GPSSpeed, gpsOdometer, GPSOdometer, vbSpeed, VBSpeed,
      odometer, Odometer, vbOdometer, VBOdometer, altitude, Altitude
    } = event;
    event.speed       = convertSpeed(speed || Speed, systemOfUnits, _userUOM);
    event.gpsSpeed    = convertSpeed(gpsSpeed || GPSSpeed, systemOfUnits, _userUOM);
    event.odometer    = convertOdometer(odometer || Odometer, systemOfUnits, _userUOM);
    event.gpsOdometer = convertOdometer(gpsOdometer || GPSOdometer, systemOfUnits, _userUOM);
    event.vbSpeed     = convertSpeed(vbSpeed || VBSpeed, systemOfUnits, _userUOM);
    event.vbOdometer  = convertOdometer(vbOdometer || VBOdometer, systemOfUnits, _userUOM);
    event.altitude    = convertAltitude(altitude || Altitude, systemOfUnits, _userUOM);
    convertPSLUnit(event, _userUOM);
  }
  return event;
};

/**
 * Returns the speed data associated with the specified event-like information
 * (which includes posted speed limit information), expressed in terms of the
 * current user's unit of measure for speed.
 *
 * ```typescript
 *   import {getEventSpeed} from '@ion/core/src/units/helpers';
 *
 *   const {speed, overSpeed, units} = getEventSpeed(item);
 *
 * ```
 *
 * @param    {EventLike} event The event containing the speed information.
 * @returns  {number, number, string} The speed, overSpeed, and speed units,
 *           converted according to the current user's chosen unit of measure.
 */
export const getEventSpeed = (event: EventLike): EventSpeed => {
  let speed = null;
  let overSpeed = null;
  let units = null;
  if (event) {
    const ev = {...event};
    if (!isNil(ev.speed) && !isNil(ev.systemOfUnits)) {
      speed = convertSpeed(ev.speed, ev.systemOfUnits as Unit, _userUOM);
      units = isImperial(_userUOM) ? 'mph' : 'kph';
      if (!isNil(ev.postedSpeedLimit) && (ev.postedSpeedLimit > 0) &&
        !isNil(ev.postedSpeedLimitUnits)) {
        convertPSLUnit(ev, _userUOM);
        const diff = speed - ev.postedSpeedLimit;
        if (diff > 0) {
          overSpeed = diff;
        }
      }
    }
  }
  return {speed, overSpeed, units};
};

/**
 * Given a specific Unit of Measure, and an Entity attribute, returns
 * the conversion measurement. This function simply delegates to the
 * getUnitByAttribute function within the attributes module.
 *
 * This function replaces the deprecated 'getUnitByType' function.
 *
 * ```typescript
 *   import {
 *     getUnitByAttribute,
 *     METRIC,
 *     SPEED,
 *     ODOMETER,
 *     ALTITUDE
 *   } from '@ion/core/src/units/helpers';
 *
 *   // get the measurement units for each attribute for a given UOM ...
 *   const speed    = getUnitByAttribute(SPEED, METRIC); // mi/h
 *   const odometer = getUnitByAttribute(ODOMETER, METRIC); // km
 *   const altitude = getUnitByAttribute(ALTITUDE, METRIC); // m
 *
 * ```
 *
 * @param   {string} attribute The attribute for which the label is
 *          to be returned.
 * @param   {string} unit The unit for which the attribute is to be
 *          converted.
 * @returns {string} The resulting unit measure for the provided entity
 *          attribute.
 */
export const getUnitByAttribute = (attribute: Attribute, unit: Unit): Measurement | string => {
  return unitFromAttribute(attribute, unit);
};

/**
 * Returns the localized translations for a given attribute for use as
 * a label or string within the application. This function simply delegates
 * to the getAttributeLabel function within the attributes module.
 *
 * This function replaces the deprecated 'getLocalizedUnit' function.
 *
 * ```typescript
 *   import {
 *     getLocalizedAttribute,
 *     SPEED,
 *     ODOMETER,
 *     ALTITUDE
 *   } from '@ion/core/src/units/helpers';
 *
 *   // get localized labels for speed, odometer, and altitude.
 *   // the labels are translated based on the browsers locale
 *   const speedLabel    = getLocalizedAttribute(SPEED);
 *   const odometerLabel = getLocalizedAttribute(ODOMETER);
 *   const altitudeLabel = getLocalizedAttribute(ALTITUDE);
 *
 * ```
 *
 * @param    {string} attribute The attribute for which the label is to
 *           be returned.
 * @returns  {string} The resulting label, or "N/A" if not supported.
 */
export const getLocalizedAttribute = (attribute: Attribute): string => {
  return getAttributeLabel(attribute, _userUOM);
};

/**
 * @private
 *
 * Provides a utility which converts the provided parameters to object
 * parameters, allowing for functions which accept a 'val', argument
 * to be optionally be called with either the 'val', 'from', and 'to'
 * arguments, or with a single object which contains 'val', 'from',
 * and 'to' properties.
 *
 * @param   {string|object} val The value which is to be converted,
 *          or an object containing 'val', 'from', and 'to' properties
 *          which define the value to be converted, the UOM from which
 *          the conversion is to be based, and the UOM to which the
 *          conversion is to be made.
 * @returns {object} An object which contains a 'val', 'from', and to
 *          properties which represent the val, from, and to parameters.
 */
const convertParams = (val: any, from: Unit, to: Unit): any => {
  const result = isObject(val) ? {...val} : {val, from, to};
  if (!isNil(result.val) && !isNil(result.from) && !isNil(result.to)) {
    return result;
  }
};

/**
 * @private
 *
 * Determines if User's current unit type is equal to US units.
 *
 * ```typescript
 *
 *   import {isUS} from '@ion/core/src/units/helpers';
 *
 *   const isUSUnits = isUS(uom);
 *
 * ```
 *
 * @param   {Unit} uom User's current unit type.
 * @returns {boolean} Equality comparison result.
 */
export const isUS = (uom: Unit): boolean => uom === Unit.US;

/**
 * @private
 *
 * Determines if User's current unit type is equal to Imperial UK units.
 *
 * ```typescript
 *
 *   import {isImperialUK} from '@ion/core/src/units/helpers';
 *
 *   const isImperialUKUnits = isImperialUK(uom);
 *
 * ```
 *
 * @param   {Unit} uom User's current unit type.
 * @returns {boolean} Equality comparison result.
 */
export const isImperialUK = (uom: Unit): boolean => uom === Unit.IMPERIAL;

/**
 * @private
 *
 * Determines if User's current unit type is equal to Imperial units Imperial US or UK.
 *
 * ```typescript
 *
 *   import {isImperial} from '@ion/core/src/units/helpers';
 *
 *   const isImperial = isImperial(uom);
 *
 * ```
 *
 * @param   {Unit} uom User's current unit type.
 * @returns {boolean} Equality comparison result.
 */
export const isImperial = (uom: Unit): boolean => (
  uom === Unit.IMPERIAL || uom === Unit.US
);

/**
 * @private
 *
 * Determines if User's current unit type is equal to Metric units.
 *
 * ```typescript
 *
 *   import {isMetric} from '@ion/core/src/units/helpers';
 *
 *   const isMetricUnits = isMetric(uom);
 *
 * ```
 *
 * @param   {Unit} uom User's current unit type.
 * @returns {boolean} Equality comparison result.
 */
export const isMetric = (uom: Unit): boolean => {
  return uom === Unit.METRIC;
};

/**
 * @private
 *
 * Returns distance value in User's units as string.
 *
 * ```typescript
 *
 *   import {getDistanceInUserUnits} from '@ion/core/src/units/helpers';
 *
 *   const distance = getDistanceInUserUnits(tracking.findNearby.miles, 'tracking:breadcrumb');
 *
 * ```
 *
 * @param   {number} miles Distance to convert.
 * @param   {string} translationEntry Path to translation entry of the unit names.
 * @returns {string} Distance in User's units as string.
 */
export const getDistanceInUserUnits = (miles: number, translationEntry: string): string => {
  const uom         = getUserUnit();
  const distance    = isMetric(uom) ? convertOdometer(miles, Unit.US, uom) : miles;
  const distanceUOM = isImperial(uom) ? 'miles' : 'kilometers';
  return `${distance} ${translate(`${translationEntry}.${distanceUOM}`)}`;
};
